/**
 * Pure-function auth helpers — no React, no module-level side effects.
 * Extracted so vitest can import them without touching the Cognito SDK.
 */

/**
 * Returns true when the error is a 403 that should trigger a visible
 * "no permission" notification to the user.
 */
export function is403(error: unknown): boolean {
  return (error as any)?.status === 403;
}

/**
 * Returns true when the error is a 404 and represents the caller's own
 * user record being absent (i.e., the account is not yet provisioned in
 * this tenant).
 *
 * The API returns 200 { provisioned: false } for own-sub misses, but
 * getPermissions receives that via the data payload, not an error.
 * This helper covers the case where the API still returns a plain 404.
 */
export function isOwnRecord404(error: unknown): boolean {
  return (error as any)?.status === 404;
}

/**
 * Returns true when an error from fetching the caller's OWN user record
 * means the account is effectively unprovisioned in this tenant.
 *
 * Covers BOTH failure shapes:
 *  - 404: no USER record (pre-R1 API, or role-holder without a profile)
 *  - 403: the user holds no ROLE_ASSIGNMENT at all, so requireRole('analyst')
 *    rejects the own-record read before the provisioned:false sentinel can
 *    ever be reached. A user who cannot even read their own record has no
 *    role — which is exactly the "unprovisioned, data will not load" state
 *    the banner exists to explain.
 */
export function isUnprovisionedError(error: unknown): boolean {
  const status = (error as any)?.status;
  return status === 404 || status === 403;
}

/**
 * Creates a debounced notifier that coalesces rapid calls into one.
 * Fires `callback` once, then ignores subsequent calls until `delayMs` has
 * elapsed after the first call.
 */
export function createDebouncedNotifier(
  callback: () => void,
  delayMs = 1500,
): () => void {
  let pending = false;
  return () => {
    if (pending) return;
    pending = true;
    setTimeout(() => {
      pending = false;
      callback();
    }, delayMs);
  };
}
